import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FilterGroup } from '../../../../models/filter-group.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeWhile } from 'rxjs/operators';
import { FilterItem } from '../../../../models/filter-item.model';
import { applyFilterAction } from '../../../../shared/utils';

const MIN_FILTER_ITEMS = 11;
const MAX_HEIGHT = 800;

@Component({
    selector: 'filter-group',
    templateUrl: './filter-group.component.html',
    styleUrls: ['./filter-group.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class FilterGroupComponent implements OnInit, OnDestroy {
    // isChanged = false; // Apply Filter button is visible only afetr user clicks on filter item
    _filter: FilterGroup;
    isSearchAvailable = true;
    filterMaxHeight = MAX_HEIGHT;
    keyUp = new Subject<KeyboardEvent>();
    flatten: boolean = false;

    private _filterItemHeight = 160;
    private _stickToBottom = false;
    private alive = true;
    private elementRef: ElementRef;

    @Output() toggledFilter = new EventEmitter<FilterGroup>();
    @Output() updatedFilter = new EventEmitter<FilterGroup>();
    @Output() changedFilter = new EventEmitter<FilterGroup>();

    @Input() set filter(val: FilterGroup) {
        if (val.label === "BRAND") {
            this.flatten = true;
        }

        this._filter = val;
        this.searchItems();
        this.isSearchAvailable = this.countFilters(this._filter) > MIN_FILTER_ITEMS;
    }

    @Input() set filterItemHeight(val) {
        if (val && !this._stickToBottom) {
            this._filterItemHeight = val;
        }
    };

    @Input() set  stickToBottom(val) {
        this._stickToBottom = val;
    }


    constructor(private el: ElementRef) {
        this.elementRef = el;
    }

    ngOnInit(): void {
        this.initSearch();
    }

    ngOnDestroy(): void {
        this.alive = false;
        // this.isChanged = false;
    }

    toggleDropdown(): void {
        this._filter.expanded = !this._filter.expanded;
        this.toggledFilter.emit(this._filter);
    }

    updateParentFilters(item): void {
        item.children.forEach((filter) => {
            filter.selected = item.selected;
        });

        this.updateFilters();
    }

    updateFilters(): void {
        this._filter.items.forEach((filter) => {
            if (filter.children.length > 0) {
                filter.selected = filter.children.every((filterChild) => {
                    return filterChild.selected === true;
                });
            }
        });

        // this.isChanged = true;

        this._filter.changed = true;
        this.changedFilter.emit(this._filter);
    }

    get isChanged(): boolean {
        return this._filter.changed;
    }


    clear(): void {
        this._filter.searchTerm = '';
        this.searchItems();

        this._filter.clearSelection();
        this._filter.clearDisplay();
        this.updatedFilter.emit(this._filter);
    }

    applyFilter(): void {
        this.updatedFilter.emit(this._filter);
    }
    // private members

    private initSearch(): void {
        this.keyUp.pipe(
            takeWhile( () => this.alive),
            map( event => event.target['value']),
            debounceTime(300),
            distinctUntilChanged(),
        ).subscribe( result => {
            this._filter.searchTerm = result;
            this.searchItems();
        })
    }

    private searchItems(): void {
        if (!this._filter) {
            return;
        }

        applyFilterAction(this._filter, (item) => this.setVisible(item, this._filter.searchTerm));
    }


    private countFilters(filterGroup: FilterGroup): number {
        let count = filterGroup.items.length;
        filterGroup.items.forEach( i => {
            count += i.children.length;
        });
        return count;
    }

    private setVisible(filterItem: FilterItem, term): void {
        if (!term) {
            filterItem.isVisible = true;
        } else if (filterItem.attr_id === 0) {
            // hide if all children not visible
            filterItem.isVisible = filterItem.children.filter(f => f.isVisible).length > 0;
        } else {
            filterItem.isVisible = filterItem.label.toLowerCase().includes(term.toLowerCase());
        }
    }


    public get styles() {
        const height = Math.max(this._filterItemHeight -  2 * 34 - (this.isSearchAvailable ? 37 : 0), 160);
        return {
            'maxHeight.px' : height
        };
    }

    public get hasSelectedItems(): boolean {
        if (!this._filter) {
            return false;
        }
        return this._filter.selectedFilters.length > 0;
    }
}
